import axios from 'axios'
import { Preferences } from '@capacitor/preferences'
import { captureException } from "@sentry/browser";
import { fetchPeriodAnalysis } from '../../helpers/dataFetcher'

// Helper functions for preferences
async function getPreference(key) {
  const result = await Preferences.get({ key });
  return result.value ? JSON.parse(result.value) : null;
}

async function setPreference(key, value) {
  return await Preferences.set({ key, value: JSON.stringify(value) });
}

async function removePreference(key) {
  return await Preferences.remove({ key });
}

// state
export const state = {
  periodDates: [],
  periodAnalysis: {},
  isLoading: false,
  isLoadingAnalysis: false,
  lastFetched: null,
  lastAnalysisFetched: null
}

// Initialize state from preferences - will be loaded in actions
// Async initialization moved to initializeFromPreferences action

// getters
export const getters = {
  periodDates: state => state.periodDates,
  periodAnalysis: state => state.periodAnalysis,
  isLoading: state => state.isLoading,
  isLoadingAnalysis: state => state.isLoadingAnalysis,
  lastFetched: state => state.lastFetched,
  lastAnalysisFetched: state => state.lastAnalysisFetched
}

// mutations - must be synchronous
export const mutations = {
  SET_PERIOD_DATES(state, dates) {
    state.periodDates = dates
  },
  SET_PERIOD_ANALYSIS(state, analysis) {
    state.periodAnalysis = analysis
  },
  SET_LOADING(state, loading) {
    state.isLoading = loading
  },
  SET_LOADING_ANALYSIS(state, loading) {
    state.isLoadingAnalysis = loading
  },
  SET_LAST_FETCHED(state, timestamp) {
    state.lastFetched = timestamp
  },
  SET_LAST_ANALYSIS_FETCHED(state, timestamp) {
    state.lastAnalysisFetched = timestamp
  },
  ADD_PERIOD_DATES(state, dates) {
    // Add new dates and remove duplicates
    const allDates = [...state.periodDates.flat(), ...dates.flat()]
    state.periodDates = [...new Set(allDates)].sort()
  },
  REMOVE_PERIOD_DATES(state, datesToRemove) {
    const flatDatesToRemove = datesToRemove.flat()
    state.periodDates = state.periodDates.filter(date => !flatDatesToRemove.includes(date))
  },
  UPDATE_PERIOD_DATES(state, { datesToAdd, datesToRemove }) {
    // Remove dates first
    if (datesToRemove && datesToRemove.length > 0) {
      const flatDatesToRemove = datesToRemove.flat()
      state.periodDates = state.periodDates.filter(date => !flatDatesToRemove.includes(date))
    }
    
    // Add new dates
    if (datesToAdd && datesToAdd.length > 0) {
      const allDates = [...state.periodDates, ...datesToAdd.flat()]
      state.periodDates = [...new Set(allDates)].sort()
    }
  },
  CLEAR_PERIOD_DATES(state) {
    state.periodDates = []
    state.lastFetched = null
  },
  CLEAR_PERIOD_ANALYSIS(state) {
    state.periodAnalysis = {}
    state.lastAnalysisFetched = null
  }
}

// actions
export const actions = {
  // Initialize store from preferences - call this on app start
  async initializeFromPreferences({ commit }) {
    try {
      const dates = await getPreference('periodDates')
      const lastFetched = await getPreference('periodDatesLastFetched')
      const analysis = await getPreference('periodAnalysis')
      const lastAnalysisFetched = await getPreference('periodAnalysisLastFetched')
      
      if (dates) {
        commit('SET_PERIOD_DATES', dates)
      }
      if (lastFetched) {
        commit('SET_LAST_FETCHED', lastFetched)
      }
      if (analysis) {
        commit('SET_PERIOD_ANALYSIS', analysis)
      }
      if (lastAnalysisFetched) {
        commit('SET_LAST_ANALYSIS_FETCHED', lastAnalysisFetched)
      }
    } catch (error) {
      console.error('Error initializing from preferences:', error)
    }
  },

  async fetchPeriodDates({ commit, state }, force = false) {
    // Don't fetch if already loading or recently fetched (within last 5 minutes), unless forced
    const now = Date.now()
    if (!force && (state.isLoading || (state.lastFetched && now - state.lastFetched < 5 * 60 * 1000))) {
      return state.periodDates
    }

    commit('SET_LOADING', true)
    
    try {
      // Clear existing period dates before fetching fresh data from DB
      commit('CLEAR_PERIOD_DATES')
      
      const response = await axios.get('/period-logs')
      commit('SET_PERIOD_DATES', response.data)
      commit('SET_LAST_FETCHED', now)
      
      // Save to preferences
      await setPreference('periodDates', response.data)
      await setPreference('periodDatesLastFetched', now)
      
      return response.data
    } catch (error) {
      captureException(error)
      console.error('Error fetching period dates:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchPeriodAnalysis({ commit, state }, force = false) {
    // Don't fetch if already loading or recently fetched (within last 5 minutes), unless forced
    const now = Date.now()
    if (!force && (state.isLoadingAnalysis || (state.lastAnalysisFetched && now - state.lastAnalysisFetched < 5 * 60 * 1000))) {
      return state.periodAnalysis
    }

    commit('SET_LOADING_ANALYSIS', true)
    
    try {
      // Clear existing period analysis before fetching fresh data from DB
      commit('CLEAR_PERIOD_ANALYSIS')
      
      const analysisData = await fetchPeriodAnalysis()
      commit('SET_PERIOD_ANALYSIS', analysisData)
      commit('SET_LAST_ANALYSIS_FETCHED', now)
      
      // Save to preferences
      await setPreference('periodAnalysis', analysisData)
      await setPreference('periodAnalysisLastFetched', now)
      
      return analysisData
    } catch (error) {
      captureException(error)
      console.error('Error fetching period analysis:', error)
      throw error
    } finally {
      commit('SET_LOADING_ANALYSIS', false)
    }
  },

  async updatePeriodDates({ dispatch }, { datesToAdd = [], datesToRemove = [] }) {
    try {
      // Update on server
      await axios.post('/update-period-dates', { 
        dates_to_delete: datesToRemove,
        dates_to_add: datesToAdd.length > 0 ? [datesToAdd] : []
      })
      
      // Fetch fresh data from backend to update store
      return await dispatch('fetchPeriodDates', true)
    } catch (error) {
      captureException(error)
      console.error('Error updating period dates:', error)
      throw error
    }
  },

  async addPeriodDates({ dispatch }, dates) {
    try {
      await axios.post('/update-period-dates', { 
        dates_to_delete: [],
        dates_to_add: [dates]
      })
      
      // Fetch fresh data from backend to update store
      return await dispatch('fetchPeriodDates', true)
    } catch (error) {
      captureException(error)
      console.error('Error adding period dates:', error)
      throw error
    }
  },

  async removePeriodDates({ dispatch }, dates) {
    try {
      await axios.post('/update-period-dates', { 
        dates_to_delete: dates,
        dates_to_add: []
      })
      
      // Fetch fresh data from backend to update store
      return await dispatch('fetchPeriodDates', true)
    } catch (error) {
      captureException(error)
      console.error('Error removing period dates:', error)
      throw error
    }
  },

  async clearPeriodDates({ commit }) {
    commit('CLEAR_PERIOD_DATES')
    // Remove from preferences
    await removePreference('periodDates')
    await removePreference('periodDatesLastFetched')
  },

  async clearPeriodAnalysis({ commit }) {
    commit('CLEAR_PERIOD_ANALYSIS')
    // Remove from preferences
    await removePreference('periodAnalysis')
    await removePreference('periodAnalysisLastFetched')
  },

  async getPeriodDatesFromPreferences() {
    return await getPreference('periodDates')
  }
}